import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Instagram, Facebook } from "lucide-react";
import { FadeIn } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TurinMap = () => (
  <div data-testid="turin-map" className="relative border border-black/10 bg-white p-2">
    <svg viewBox="0 0 600 420" className="w-full h-auto block" role="img" aria-label="Mappa stilizzata di Rivalta e Torino">
      <rect width="600" height="420" fill="#FFFFFF" />
      {Array.from({ length: 11 }).map((_, i) => (
        <line key={`v${i}`} x1={40 + i * 52} y1="0" x2={40 + i * 52} y2="420" stroke="rgba(28,27,26,0.06)" strokeWidth="1" />
      ))}
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`h${i}`} x1="0" y1={30 + i * 52} x2="600" y2={30 + i * 52} stroke="rgba(28,27,26,0.06)" strokeWidth="1" />
      ))}
      <path d="M 60 400 C 180 330, 260 300, 320 220 S 480 60, 580 20" fill="none" stroke="#3B6EA5" strokeOpacity="0.45" strokeWidth="12" strokeLinecap="round" />
      <path d="M 20 120 C 140 160, 260 180, 340 210" fill="none" stroke="#3B6EA5" strokeOpacity="0.3" strokeWidth="7" strokeLinecap="round" />
      <path d="M 100 360 C 140 220, 220 120, 380 70 S 540 60, 590 110" fill="none" stroke="#E30613" strokeOpacity="0.45" strokeWidth="2" strokeDasharray="8 8" />
      <circle cx="320" cy="230" r="5" fill="#1C1B1A" opacity="0.5" />
      <text x="320" y="262" fill="#1C1B1A" fontSize="14" fontWeight="600" fontFamily="Manrope" letterSpacing="3" textAnchor="middle" opacity="0.6">TORINO</text>
      <circle cx="120" cy="330" r="4" fill="#1C1B1A" opacity="0.35" />
      <text x="132" y="335" fill="#1C1B1A" fontSize="11" fontFamily="Manrope" letterSpacing="2" opacity="0.5">ORBASSANO</text>
      <circle cx="470" cy="120" r="4" fill="#1C1B1A" opacity="0.35" />
      <text x="440" y="105" fill="#1C1B1A" fontSize="11" fontFamily="Manrope" letterSpacing="2" opacity="0.5">SETTIMO T.SE</text>
      <circle cx="160" cy="210" r="7" fill="#E30613" />
      <circle cx="160" cy="210" r="7" fill="none" stroke="#E30613" strokeWidth="2" className="map-pulse" />
      <text x="160" y="245" fill="#E30613" fontSize="15" fontWeight="800" fontFamily="Manrope" letterSpacing="3" textAnchor="middle">RIVALTA DI TORINO</text>
      <text x="24" y="40" fill="#1C1B1A" fontSize="10" fontFamily="Manrope" letterSpacing="3" opacity="0.4">FKL S.R.L.S. — AREA OPERATIVA · TORINO E PROVINCIA</text>
    </svg>
  </div>
);

const inputCls =
  "w-full rounded-none bg-white border border-black/15 px-5 py-4 text-base text-[#1C1B1A] placeholder:text-[#1C1B1A]/40 outline-none focus:border-[#E30613] transition-[border-color,background-color] duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Richiesta inviata! Ti ricontatteremo entro 24 ore.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Invio non riuscito. Riprova o chiamaci direttamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" data-testid="contact-section" className="bg-white border-t border-black/10">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-28 lg:py-36">
        <FadeIn>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#1C1B1A]"><span className="inline-block border-b border-[#E30613] pb-1">Contatti</span></h2>
        </FadeIn>

        <div className="mt-14 grid md:grid-cols-2 gap-12 md:gap-0">
          <FadeIn className="md:pr-14 flex flex-col">
            <p data-testid="contact-intro" className="text-base sm:text-lg leading-loose text-[#1C1B1A]/80 max-w-md">
              Hai un progetto da realizzare o un intervento da valutare?
              Siamo a disposizione per offrirti soluzioni affidabili e su misura.
              Contattaci per una consulenza o un preventivo gratuito.
            </p>
            <img src="/fkl-logo.png" alt="FKL srls" className="mt-auto pt-14 w-40 sm:w-52" />
          </FadeIn>

          <FadeIn delay={0.15} className="md:border-l md:border-[#1C1B1A]/25 md:pl-14">
            <div data-testid="contact-company-info" className="text-base sm:text-lg leading-loose text-[#1C1B1A]">
              <div className="font-semibold">FKL S.r.l.s.</div>
              <div>Viale Vif, 12 - 10040 Rivalta di Torino (TO)</div>
              <div className="mt-6">Tel. <a href="tel:+393272239520" className="hover:text-[#E30613] transition-colors duration-300">+39 327 223 9520</a></div>
              <div><a href="mailto:fkl.official1@gmail.com" className="hover:text-[#E30613] transition-colors duration-300">fkl.official1@gmail.com</a></div>
              <div>P.I. 12481530017</div>
            </div>
            <div className="mt-8 flex items-center gap-3">
              <a data-testid="social-instagram-link" href="https://www.instagram.com/fkl.official_" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-11 h-11 rounded-full bg-[#1C1B1A] flex items-center justify-center text-white hover:bg-[#E30613] transition-colors duration-300">
                <Instagram className="w-[18px] h-[18px]" strokeWidth={1.75} />
              </a>
              <a data-testid="social-facebook-link" href="https://www.facebook.com/share/1JpDVsqP4t/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-11 h-11 rounded-full bg-[#1C1B1A] flex items-center justify-center text-white hover:bg-[#E30613] transition-colors duration-300">
                <Facebook className="w-[18px] h-[18px]" strokeWidth={1.75} />
              </a>
            </div>
          </FadeIn>
        </div>
      </div>

      <div className="bg-[#F4F1EA] border-t border-black/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-start">
          <FadeIn>
            <h3 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#1C1B1A]">
              Torino e provincia
            </h3>
            <p className="mt-5 max-w-md text-base leading-loose text-[#1C1B1A]/70">
              Operiamo regolarmente anche su tutto il territorio regionale di Piemonte e Lombardia.
            </p>
            <div className="mt-8">
              <TurinMap />
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <form data-testid="quote-form" onSubmit={submit} className="flex flex-col gap-6">
              <div>
                <h3 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#1C1B1A]">Richiedi un preventivo gratuito</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#1C1B1A]/60">Compila il modulo: ti ricontattiamo entro 24 ore lavorative.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <input data-testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder="Nome e cognome" className={inputCls} />
                <input data-testid="quote-phone-input" required value={form.phone} onChange={set("phone")} placeholder="Telefono" type="tel" className={inputCls} />
              </div>
              <input data-testid="quote-email-input" required value={form.email} onChange={set("email")} placeholder="Email" type="email" className={inputCls} />
              <textarea
                data-testid="quote-message-input"
                required
                value={form.message}
                onChange={set("message")}
                placeholder="Raccontaci il tuo progetto (es. ristrutturazione bagno, rifacimento tetto...)"
                rows={5}
                className={`${inputCls} resize-none`}
              />
              <button
                data-testid="quote-submit-button"
                type="submit"
                disabled={sending}
                className="inline-flex items-center justify-center gap-3 rounded-full bg-[#E30613] px-9 py-5 text-xs font-bold uppercase tracking-[0.2em] text-white transition-[transform,background-color] duration-300 hover:bg-[#B3050F] hover:scale-[1.02] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} />}
                {sending ? "Invio in corso..." : "Invia la richiesta"}
              </button>
              <p className="text-xs text-[#1C1B1A]/45">Inviando accetti di essere ricontattato da FKL S.r.l.s. Nessuno spam, solo il tuo preventivo.</p>
            </form>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}
